import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';

//hoc
import withLocalization from '~/hoc/withLocalization';

//components
import Checkbox from '~/components/CustomCheckbox/CustomCheckbox';
import Button from '~/components/CustomButton/CustomButton';
import { ProjectsWidget } from '~/components/GenericForm/Widgets/ProjectsWidget';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';

//assets
import CloseIcon from '~/assets/img/modal-close-icon.svg';
import ActivateUser from '~/assets/img/ActivateUser.svg';

const PAGE_SIZE = 10;

const ImportTaskFromIntegration = ({ t, taskStore, commonStore, handleImportModal }) => {
    const [loading, setLoading] = useState(false);
    const [activities, setActivities] = useState([]);
    const [from, setFrom] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [selectedActivities, setSelectedActivities] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [importing, setImporting] = useState(false);

    const loadData = async (fromOffset) => {
        setLoading(true);
        try {
            const { list = [], from = 0, hasMore = false } = await taskStore.getTasksFromIntegration({ from: fromOffset, count: PAGE_SIZE });
            setActivities(list);
            setFrom(from);
            setHasMore(hasMore);
            setSelectedActivities(list.filter(ele => !ele.isSynced));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData(0);
    }, []);

    const handleNext = () => {
        if (hasMore) loadData(from + PAGE_SIZE);
    };

    const handlePrev = () => {
        if (from > 0) loadData(Math.max(0, from - PAGE_SIZE));
    };

    const handleCheckbox = (ele, checked) => {
        if (checked)
            setSelectedActivities(prev => [...prev, ele]);
        else
            setSelectedActivities(prev => prev.filter(item => item.activity.id !== ele.activity.id));
    };

    const isSelected = (ele) => selectedActivities.some(item => item.activity.id === ele.activity.id);

    const handleImport = async () => {
        setImporting(true);
        try {
            const res = await taskStore.saveTasksFromIntegrationTripletex({
                projectId: selectedProject,
                activities: selectedActivities.map(ele => ele.activity),
            });
            commonStore.addNotification(t(res.message || 'Activities imported successfully'), null, 'success');
            handleImportModal(false);
        } catch (err) {
            console.error(err);
            commonStore.addNotification(t('Something went wrong'), null, 'error');
        } finally {
            setImporting(false);
        }
    };

    return (
        <Modal size="lg" className="modal fade" show={true} onHide={() => handleImportModal(false)}>
            <Modal.Header>
                <Modal.Title>{t('Import Activities From')} {commonStore.config?.integration_details?.product}</Modal.Title>
                <div className='modal-header__revised__closebtn' onClick={() => handleImportModal(false)}>
                    <img src={CloseIcon} className="cursor-pointer" alt='close_button' />
                </div>
            </Modal.Header>
            <Modal.Body className='mb-2'>
                {loading ? <LoadingSpinner /> :
                    <>
                        <div className='d-flex justify-content-between align-items-center mb-2 w-100'>
                            <span className='fw-bold font-size-15'>
                                {t('Select activities to import')}
                            </span>
                            <div className='d-flex align-items-center gap-2'>
                                <Button
                                    disabled={from === 0}
                                    onClick={handlePrev}
                                >
                                    <i className="fa fa-arrow-left me-1" aria-hidden="true"></i>
                                    {t('Prev')}
                                </Button>
                                <Button fill wd
                                    disabled={!hasMore}
                                    onClick={handleNext}
                                >
                                    {t('Next')}
                                    <i className="fa fa-arrow-right ms-1" aria-hidden="true"></i>
                                </Button>
                            </div>
                        </div>
                        <div className='mb-3'>
                            <div className='d-flex align-items-center gap-1 mb-1'>
                                <span className='fw-bold font-size-14'>{t('Choose a project for these activities')}</span>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="import-project-tooltip">
                                            {t('Select a project where the imported activities will be created as tasks. All selected activities will be added to the chosen project.')}
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle ms-1 text-secondary cursor-pointer" aria-hidden="true" />
                                </OverlayTrigger>
                            </div>
                            <ProjectsWidget
                                value={selectedProject}
                                onChange={(id) => setSelectedProject(id)}
                                menuPlacement="auto"
                                maxMenuHeight={200}
                            />
                        </div>
                        <div className='projectMemberForm'>
                            <Table className='w-100'>
                                <thead>
                                    <tr>
                                        <th className='text-start'>{t('Activity')} {t('Name')}</th>
                                        <th>{t('Synced')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activities.length === 0 &&
                                        <tr><td colSpan={2} className='text-center'>{t('No Data Found')}</td></tr>
                                    }
                                    {activities.map((ele) => (
                                        <tr key={ele.activity.id} className='text-capitalize mt-1'>
                                            <td className='text-start'>{ele.activity.name}</td>
                                            <td className='timelog_array_details'>
                                                {ele.isSynced
                                                    ? <img src={ActivateUser} alt='tick' title={`${t('Already')} ${t('Synced')}`} />
                                                    : <Checkbox
                                                        isChecked={isSelected(ele)}
                                                        onChange={checked => handleCheckbox(ele, checked)}
                                                        wrapperClassName={'bulk-shift-chekbox-style'}
                                                    />
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </>
                }
            </Modal.Body>
            <Modal.Footer>
                <Button fill wd
                    disabled={!selectedProject || selectedActivities.length === 0 || importing}
                    onClick={handleImport}
                >
                    {importing
                        ? <><i className="fa fa-spinner fa-spin me-1" aria-hidden="true"></i>{t('Importing')}...</>
                        : t('Import')
                    }
                </Button>
                <Button wd onClick={() => handleImportModal(false)}>
                    {t('Cancel')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default inject('commonStore', 'taskStore')(withLocalization(observer(ImportTaskFromIntegration)));
